import React, { Fragment } from 'react';
import {
    FREQUENCY,
    NAME_MISSING,
    EMAIL_MISSING,
    OCCUPATION_MISSING,
    LOCATION_MISSING,
    NO_JOBS_FOUND,
    CURRENCY_MISSING,
    PAY_FREQUENCY_LIST_BI_WEEKLY,
    PAY_FREQUENCY_LIST_PER_JOB,
} from './constants';

import _get from 'lodash/get';
import _capitalize from 'lodash/capitalize';
import _split from 'lodash/split';
import _join from 'lodash/join';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import _defaultTo from 'lodash/defaultTo';
import _find from 'lodash/find';
import _isNumber from 'lodash/isNumber';
import { getCompanySizeOptions, getYearsOfExperienceOptions } from './lovHelper';
import { getYear } from 'date-fns';
import { AFITD_COMPANY_INC_NAME } from '../system/constants';

export const capitalizeAllWords = (value = '') => {
    return _join(
        _map(_split(value, ' '), item => _capitalize(item)),
        ' ',
    );
};

export const numberWithCommas = (x = 0) => {
    return isNaN(x) ? '0' : x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const getFormattedPrice = (amount, currencySymbol = '$') => {
    return `${amount < 0 ? '-' : ''}${currencySymbol}${numberWithCommas(parseFloat(Math.abs(amount)).toFixed(2))}`;
};

export const getNumberWithoutCommas = x => {
    return x.replace(/,/g, '');
};

/**
 * Format the salary/rate info.
 * If min rate and max rate are the same, treat it as exact and display only one number.
 *
 * @param {*} isExactRate
 * @param {*} rateMin
 * @param {*} rateMax
 * @param {*} currency
 * @param {*} payFrequency
 */
export const getSalaryRateText = (isExactRate, rateMin, rateMax, currency, payFrequency) => {
    const rateMinFormatted = numberWithCommas(_defaultTo(rateMin, 0));
    const rateMaxFormatted = numberWithCommas(_defaultTo(rateMax, 0));
    const frequency = payFrequency === 'Bi-Weekly' ? PAY_FREQUENCY_LIST_BI_WEEKLY : FREQUENCY[payFrequency];
    let suffix = `${currency || ''} ${frequency ? `per ${frequency}` : ''}`;
    if (payFrequency === PAY_FREQUENCY_LIST_PER_JOB) {
        suffix = `${currency || ''} per Job`;
    }
    return isExactRate || rateMinFormatted === rateMaxFormatted ? `${rateMinFormatted} ${suffix}` : `${rateMinFormatted} - ${rateMaxFormatted} ${suffix}`;
};

export const getSalaryRateFilterText = (rateMin, currencySymbol = '$', payFrequency) => {
    const rateMinFormatted = numberWithCommas(rateMin);
    const frequency = FREQUENCY[payFrequency];
    let suffix = `${frequency ? `per ${frequency}` : ''}`;
    if (payFrequency === PAY_FREQUENCY_LIST_PER_JOB) {
        suffix = `per Job`;
    }
    return `${currencySymbol}${rateMinFormatted}+ ${suffix}`;
};

export const getMaxSalaryRateFilterText = (rateMin, currencySymbol = '$', payFrequency) => {
    const rateMinFormatted = numberWithCommas(rateMin);
    const frequency = FREQUENCY[payFrequency];
    let suffix = `${frequency ? `per ${frequency}` : ''}`;
    if (payFrequency === PAY_FREQUENCY_LIST_PER_JOB) {
        suffix = `per Job`;
    }
    return `${currencySymbol}${rateMinFormatted} max ${suffix}`;
};

export const getFullNameText = (firstName, middleName, lastName) => {
    const nameArray = [firstName || '', middleName || '', lastName || ''];
    const nameJoined = nameArray.join(' ');
    const name = nameJoined.replace(/\s\s+/g, ' ').trim();
    return name || NAME_MISSING;
};

export const getFullNameByUser = user => {
    const firstName = _get(user, 'firstName', '');
    const middleName = _get(user, 'middleName', '');
    const lastName = _get(user, 'lastName', '');

    return getFullNameText(firstName, middleName, lastName);
};

export const getFullNameMiddleInitialByUser = user => {
    const firstName = _get(user, 'firstName', '');
    const middleInitial = _defaultTo(_get(user, 'middleName'), '').charAt(0);
    const lastName = _get(user, 'lastName', '');

    return getFullNameText(firstName, middleInitial ? `${middleInitial}.` : '', lastName);
};

export const getNameByUser = user => {
    const firstName = _get(user, 'firstName', '');
    const lastName = _get(user, 'lastName', '');

    return getFullNameText(firstName, null, lastName);
};

export const getEmailText = email => {
    return email || EMAIL_MISSING;
};

export const getFirstOccupationText = (occupationList, resumeList = []) => {
    // return _get(occupationList, '[0]', OCCUPATION_MISSING);
    let occupationText = _get(occupationList, '[0]');
    if (!occupationText) {
        const activeResume = _find(resumeList, item => item.isActive);
        occupationText = _get(activeResume, 'title', OCCUPATION_MISSING);
    }
    return occupationText;
};

export const getCityStateText = (city, state) => {
    const locationText = _reduce(
        [city, state],
        (location, currentVal) => {
            return location ? (currentVal ? `${location}, ${currentVal}` : '') : currentVal;
        },
        '',
    );
    return locationText || LOCATION_MISSING;
};

export const getCityStateCountryText = (city, state, country) => {
    const locationText = _reduce(
        [city, state, country],
        (location, currentVal) => {
            return location ? (currentVal ? `${location}, ${currentVal}` : '') : currentVal;
        },
        '',
    );
    return locationText || LOCATION_MISSING;
};

export const getJobsFoundText = (jobsCount = 0) => {
    if (jobsCount === 0) {
        return NO_JOBS_FOUND;
    } else {
        return `${jobsCount} job${jobsCount > 1 ? 's' : ''} found`;
    }
};

export const getEngagementRemainingText = engagementRemainingCount => {
    if (engagementRemainingCount === null) {
        return `You have unlimited engagements for this billing period`;
    } else {
        return (
            <span>
                <span>You have </span>
                <span style={{ textDecoration: 'underline' }}>{engagementRemainingCount}</span>
                <span> engagement{engagementRemainingCount === 1 ? '' : 's'} remaining for this billing period</span>
            </span>
        );
    }
};

export const getCurrencyText = (code, symbol) => {
    if (!code) {
        return CURRENCY_MISSING;
    } else {
        return `${symbol ? symbol + ' - ' : ''}${code}`;
    }
};

export const getCompanySizeText = (companySize, defaultValue = 'Company Size Missing') => {
    const companySizeOptions = getCompanySizeOptions();
    const companySizeItem = _find(companySizeOptions, item => item.minVal === companySize);
    return companySizeItem ? companySizeItem.description : defaultValue;
};

export const getYearsOfExperienceText = (yearsOfExperience, defaultValue) => {
    const options = getYearsOfExperienceOptions();
    const yearsOfExperienceItem = _find(options, item => item.minVal === yearsOfExperience);
    return yearsOfExperienceItem ? yearsOfExperienceItem.description : defaultValue;
};

export const getJobYearsOfExperienceText = yrsOfExp => {
    if ((!yrsOfExp && yrsOfExp !== 0) || !_isNumber(yrsOfExp) || yrsOfExp === 0) {
        return `0 Years of Experience`;
    } else if (yrsOfExp < 12) {
        return `Under 1 Year of Experience`;
    } else if (yrsOfExp === 60) {
        return `5 Years of Experience`;
    } else {
        const numberOfYears = yrsOfExp / 12;
        return `${numberOfYears}+ Years of Experience`;
    }
};

export const nl2br = text => {
    return _map(_split(text, '\n'), (item, idx) => (
        <Fragment key={idx}>
            {item}
            <br />
        </Fragment>
    ));
};

export const checkSpaceInString = text => {
    return /\s/g.test(text);
};
export const getMemberDetail = ({ currentUser, userId, currentEmployer, employerId }) => {
    const isEmp = !!employerId;
    let firstName = _get(currentUser, 'firstName', 'First');
    let lastName = _get(currentUser, 'lastName', 'Last');
    let middleName = currentUser?.middleName || '';
    let email;
    let profilePicUrl;
    let role;
    if (isEmp) {
        const members = _get(currentEmployer, 'members');
        const member = _find(members, item => item.userId === userId);
        email = member?.email;
        middleName = member?.middleName || '';
        profilePicUrl = member?.profilePicUrl;
        role = (member?.isOwner && 'Owner / Admin') || (member?.isAdmin && 'Adminstration') || 'Member';
        firstName = _get(member, 'firstName', 'First');
        lastName = _get(member, 'lastName', 'Last');
    }

    return [firstName, lastName, email, profilePicUrl, role, middleName];
};

export const getCopyrightYears = () => {
    const now = new Date();
    const currentYear = getYear(now);
    return currentYear > 2022 ? `2022-${currentYear}` : '2022';
};

export const getCopyrightText = ({ variant } = {}) => {
    if (variant === 'small') {
        return `©${getCopyrightYears()} ${AFITD_COMPANY_INC_NAME}`;
    } else {
        return `© Copyright ${getCopyrightYears()} ${AFITD_COMPANY_INC_NAME} All rights reserved.`;
    }
};
export const getCopyrightTextInternship = ({ variant } = {}) => {
    if (variant === 'small') {
        return `©${getCopyrightYears()} ${AFITD_COMPANY_INC_NAME}`;
    } else {
        return `©${getCopyrightYears()} ${AFITD_COMPANY_INC_NAME}`;
    }
};
